

/**
 * AppContainer - Contenedor para la app activa
 *
 * Muestra la app activa basada en activeTabId.
 * - Si no hay tab activo: muestra welcome message
 * - Si hay tab activo: renderiza la app correspondiente
 * - Error Boundary atrapa crashes sin matar el Shell
 */

import { Suspense, useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useShellStore } from '@/store/shell-store'
import { useAuth } from '@/contexts/AuthContext'
import { getAppComponent } from '@/apps/_registry'
import { colors, typography, spacing } from '@/lib/tokens'
import { AppErrorBoundary } from './AppErrorBoundary'

const SUMMA_PLUS_PATH = 'M74.332 24.494v12.609c0 2.153-1.716 3.9-3.833 3.9s-3.833-1.747-3.833-3.9v-12.61h-11.89c-2.097 0-3.798-1.715-3.798-3.832s1.7-3.833 3.798-3.833h11.89V4.902c0-2.154 1.716-3.9 3.833-3.9s3.833 1.746 3.833 3.9v11.926H87.18c2.097 0 3.797 1.716 3.797 3.833s-1.7 3.833-3.797 3.833H74.33z'
const SUMMA_SQUARE_PATH = 'M83.63 41.312V79.858A3.69 3.69 0 0 1 79.94 83.549H12.015A3.685 3.685 0 0 1 8.326 79.86V11.932A3.681 3.681 0 0 1 12.007 8.242H50.897V0.897H12.007C5.916 0.896 0.978 5.831 0.978 11.932V79.86C0.978 85.955 5.913 90.896 12.014 90.896H79.943C86.037 90.896 90.978 85.954 90.978 79.858V41.312Z'
const SUMMA_ICON_PATH = SUMMA_PLUS_PATH + SUMMA_SQUARE_PATH
const SUMMA_SQUARE_OUTLINE = 'M90.978 41.312 V79.858 C90.978 85.954 86.037 90.896 79.943 90.896 H12.014 C5.913 90.896 0.978 85.955 0.978 79.86 V11.932 C0.978 5.831 5.916 0.896 12.007 0.897 H50.897'

function LoadingFallback() {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      width: '100%',
      background: colors.background,
    }}>
      <div style={{ position: 'relative', width: 48, height: 48 }}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"
          style={{ position: 'absolute', width: 48, height: 48, overflow: 'hidden' }}>
          <g fill={colors.border} opacity={0.2}>
            <path d={SUMMA_ICON_PATH} fillRule="evenodd"/>
          </g>
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"
          style={{ position: 'absolute', width: 48, height: 48, overflow: 'hidden' }}>
          <path d={SUMMA_SQUARE_OUTLINE}
            fill="none" stroke={colors.primary} strokeWidth="7" strokeLinecap="round"
            className="summa-trace-sq" />
        </svg>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"
          style={{ position: 'absolute', width: 48, height: 48 }}>
          <g fill={colors.primary} className="summa-flash-plus">
            <path d={SUMMA_PLUS_PATH} fillRule="evenodd"/>
          </g>
        </svg>
      </div>
      <style>{`
        .summa-trace-sq {
          stroke-dasharray: 270;
          stroke-dashoffset: 270;
          animation: trace-sq 2.4s ease-in-out infinite;
        }
        .summa-flash-plus {
          opacity: 0;
          animation: flash-plus 2.4s ease-in-out infinite;
        }
        @keyframes trace-sq {
          0%   { stroke-dashoffset: 270; opacity: 0; }
          3%   { opacity: 1; }
          75%  { stroke-dashoffset: 0; opacity: 1; }
          80%  { opacity: 0; }
          100% { stroke-dashoffset: 0; opacity: 0; }
        }
        @keyframes flash-plus {
          0%   { opacity: 0; }
          78%  { opacity: 0; }
          80%  { opacity: 1; }
          88%  { opacity: 1; }
          92%  { opacity: 0; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  )
}

function WelcomeScreen() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        background: colors.background,
        gap: spacing.lg,
      }}
    >
      <div style={{ fontSize: '48px', opacity: 0.3 }}>
        📊
      </div>
      <div
        style={{
          fontSize: typography.fontSize.lg,
          fontFamily: typography.fontFamily.sans,
          color: colors.textSecondary,
          textAlign: 'center',
        }}
      >
        Usa <kbd style={{
          padding: '4px 8px',
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          fontFamily: typography.fontFamily.mono,
          fontSize: typography.fontSize.sm,
          color: colors.textPrimary,
        }}>⌘K</kbd> para abrir una aplicación
      </div>
      <div style={{
        fontSize: typography.fontSize.sm,
        color: colors.textMuted,
      }}>
        O usa el botón <span style={{ color: colors.primary }}>+</span> en el header
      </div>
    </div>
  )
}

export function AppContainer() {
  const { activeTabId, openTabs, closeTab, enableUat, orgParams } = useShellStore()
  const { user } = useAuth()
  const getToken = useMemo(() => user ? () => user.getIdToken() : undefined, [user])
  const [retryKey, setRetryKey] = useState(0)
  const [registryRetries, setRegistryRetries] = useState(0)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleRetry = useCallback(() => {
    setRetryKey(prev => prev + 1)
  }, [])

  const handleRemove = useCallback(() => {
    if (activeTabId) {
      closeTab(activeTabId)
    }
  }, [activeTabId, closeTab])

  // Find the active tab
  const activeTab = openTabs.find(tab => tab.tabId === activeTabId)

  // Get the app component from registry (cached, same reference every render)
  // Retry: registry may not be populated yet on first load (race condition)
  const appId = activeTab?.appId
  const AppComponent = appId ? getAppComponent(appId) : null

  useEffect(() => {
    if (!appId || AppComponent || registryRetries >= 5) return
    retryTimerRef.current = setTimeout(() => {
      setRegistryRetries(prev => prev + 1)
    }, 500)
    return () => { if (retryTimerRef.current) clearTimeout(retryTimerRef.current) }
  }, [appId, AppComponent, registryRetries])

  // Reset retries when tab changes
  useEffect(() => {
    setRegistryRetries(0)
  }, [appId])

  // If no active tab, show welcome message
  if (!activeTab) {
    return <WelcomeScreen />
  }

  if (!AppComponent && registryRetries < 5) {
    return <LoadingFallback />
  }

  if (!AppComponent) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        gap: '12px',
        background: colors.background,
      }}>
        <div style={{ color: colors.primary, fontSize: '14px', fontWeight: 600, fontFamily: typography.fontFamily.mono }}>
          App not found: {activeTab.appId}
        </div>
        <div style={{ color: colors.textMuted, fontSize: '12px', fontFamily: typography.fontFamily.mono, textAlign: 'center', maxWidth: '400px' }}>
          Check Firestore applications/{activeTab.appId} — remote_url may be empty.
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            onClick={handleRetry}
            style={{
              padding: '6px 16px',
              background: 'transparent',
              border: `1px solid ${colors.primary}`,
              borderRadius: '4px',
              color: colors.primary,
              fontSize: '12px',
              fontFamily: typography.fontFamily.mono,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
          <button
            onClick={handleRemove}
            style={{
              padding: '6px 16px',
              background: 'transparent',
              border: `1px solid ${colors.border}`,
              borderRadius: '4px',
              color: colors.textSecondary,
              fontSize: '12px',
              fontFamily: typography.fontFamily.mono,
              cursor: 'pointer',
            }}
          >
            Close tab
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
      <AppErrorBoundary
        key={`${activeTab.instanceId}-${retryKey}`}
        appId={activeTab.appId}
        onRetry={handleRetry}
        onRemove={handleRemove}
      >
        <Suspense fallback={<LoadingFallback />}>
          <AppComponent
            instanceId={activeTab.instanceId}
            user={user ? { uid: user.uid, email: user.email || '', displayName: user.displayName || '' } : undefined}
            getToken={getToken}
            enableUat={enableUat}
            orgParams={orgParams}
          />
        </Suspense>
      </AppErrorBoundary>
    </div>
  )
}
